import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetLoginUserprivilege } from '../../../../../api/auth/auth';
import { useGetPsHistoryList } from '../../../../../api/pss/pss';
import { useDefaultOptions } from '../../../../../hooks/api';
import * as sessionStorageUtils from '../../../../../utils/sessionStorage';
import NoDataComponent from '../../../../atoms/NoDataComponent';
import ErrorModal from '../../../../modules/ErrorModal';
import Modal from '../../../../modules/Modal';
import PageNavi from '../../../../modules/PageNavi';
import SpinnerModal from '../../../../modules/SpinnerModal';
import {
  DialogType,
  getCheckMarkTypeFromDialogType,
  getDialogTypeFromTempStateForViewMode,
  getDialogTypeStr,
  getFormatDateYYYYMMDD,
  getTempState,
} from '../PsListCommmon';
import { SortableTh } from '../PsListForm';
import PsApprovalDialog from './PsApprovalDialog';
import PsCheckAddDialog from './PsCheckAddDialog';
import PsCheckDialog from './PsCheckDialog';
import PsHmSubmitDialog from './PsHmSubmitDialog';

interface Props {
  /** 状態履歴ダイアログを画面から消すための関数 */
  onClose: () => void;
  /** バージョンID */
  versionId: number | undefined;
  /** 画面更新用の関数。親画面を更新する場合、更新用のstateを渡す。 */
  setRefresh: () => void;
}

/**
 * 状態履歴ダイアログ
 */
function PsStateHistoryDialog({ onClose, versionId, setRefresh }: Props) {
  // 翻訳用定数
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const options = useDefaultOptions(language);

  // 1画面内に表示するデータ数
  const N_DATA_PER_PAGE = 10;

  // ダイアログ表示
  const [activeModal, setActiveModal] = useState(DialogType.NONE);
  const psTemporaryStateHistoryId = useRef<number>(-1);

  // 状態履歴一覧表示取得
  const psHistoryListApi = useGetPsHistoryList(versionId ?? 0, options);

  // ログインユーザ情報取得
  const psLoginUserInfo = useGetLoginUserprivilege(options);

  // ページング処理
  const [currentPage, setCurrentPage] = useState(1);
  /** ソート */
  const [sortInfo, setSortInfo] = useState(
    sessionStorageUtils.get('Pss.SortInfo', { key: ['updated_at'], isDisc: true })
  );
  /** ソート処理 */
  const allPss = useMemo(
    () =>
      [...(psHistoryListApi?.data?.data.ps_temp_histories ?? [])].sort((a, b) => {
        const aAny = a as { [key: string]: string | undefined };
        const bAny = b as { [key: string]: string | undefined };
        const multiValue = sortInfo.isDisc ? -1 : 1;

        // 配列にソートキーがセットされていない場合は何もしない
        if (sortInfo.key.length === 0) {
          return 0;
        }

        // 配列にソートキーが1つだけの場合
        if (sortInfo.key.length === 1) {
          return ((aAny[sortInfo.key[0]] ?? '') > (bAny[sortInfo.key[0]] ?? '') ? 1 : -1) * multiValue;
        }

        // 配列にソートキーが2つ以上の場合(2つとみなして並び替え)
        // （所属 + 作成者)はソートキーが2つある
        if ((aAny[sortInfo.key[0]] ?? '') !== (bAny[sortInfo.key[0]] ?? '')) {
          return ((aAny[sortInfo.key[0]] ?? '') > (bAny[sortInfo.key[0]] ?? '') ? 1 : -1) * multiValue;
        }
        return ((aAny[sortInfo.key[1]] ?? '') > (bAny[sortInfo.key[1]] ?? '') ? 1 : -1) * multiValue;
      }),
    [sortInfo, psHistoryListApi.data]
  );

  // ページング番号にあわせてフィルタリング
  const filteredPss = useMemo(
    () => allPss.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, allPss]
  );

  /** ソートキーを保存 */
  const changeSortKey = useCallback((key: string[]) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: JSON.stringify(pre.key) === JSON.stringify(key) && !pre.isDisc };
      sessionStorageUtils.set('PsState.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);

  // 選択中ページ情報保持関数
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('PsState.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  useEffect(() => {
    if (psHistoryListApi.data?.data.ps_temp_histories != null) {
      setCurrentPage(1);
    }
  }, [psHistoryListApi.data?.data.ps_temp_histories]);

  /**
   *
   * @param tempState
   * @param isView
   * @param stateHistoryId
   */
  function callModal(tempState: number, tempStateHistoryId: number) {
    setActiveModal(tempState);
    psTemporaryStateHistoryId.current = tempStateHistoryId;
  }

  // ログインユーザ情報
  if (psLoginUserInfo.isLoading) {
    return <SpinnerModal />;
  }
  if (psLoginUserInfo.isError || psLoginUserInfo.data?.data == null) {
    return <ErrorModal onClickOk={onClose} error={psLoginUserInfo.error} />;
  }

  // ローディング
  if (psHistoryListApi.isLoading) {
    return <SpinnerModal />;
  }

  // エラー時
  if (psHistoryListApi.isError) {
    return <ErrorModal onClickOk={onClose} error={psHistoryListApi.error} />;
  }

  // データがない場合
  if (psHistoryListApi.data.data.ps_temp_histories == null) {
    return <NoDataComponent />;
  }

  return (
    <>
      <Modal active onClose={onClose} displayCloseButton={false} additionalClassName="popupfr1030">
        <div className="pop-tablefr1">
          <div>
            <table>
              {/* 状態履歴ダイアログ 画面上部 */}
              <tbody>
                <tr>
                  <th>{t('管理No.')}</th>
                  <td>{psHistoryListApi.data.data.ps_no}</td>
                </tr>
                <tr>
                  <th>{t('構成')}</th>
                  <td>
                    {/* [構成]は配列のオブジェクト 改行して1つにまとめる */}
                    {psHistoryListApi.data.data.composition?.map((val, index) =>
                      (() => {
                        if (index === (psHistoryListApi.data.data.composition?.length ?? 0) - 1) {
                          return <React.Fragment key={val}>{val}</React.Fragment>;
                        }
                        return (
                          <React.Fragment key={val}>
                            {val}
                            <br />
                          </React.Fragment>
                        );
                      })()
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <table>
              <tbody>
                <tr>
                  <th>{t('タイトル')}</th>
                  <td>{psHistoryListApi.data.data.title}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="pop-tablefr2">
          <h3>{t('承認履歴')}</h3>
          <table className="poptable2">
            {/* 承認履歴リスト */}
            <tbody>
              {/* ラベル クリックで並び替え */}
              <tr>
                <SortableTh label={t('状態')} onClick={() => changeSortKey(['temp_state'])} />
                <SortableTh label={t('差戻の理由')} onClick={() => changeSortKey(['description'])} />
                <th>{}</th>
                <SortableTh
                  label={t('更新者')}
                  onClick={() => changeSortKey(['update_user_membership_name', 'update_user'])}
                />
                <SortableTh label={t('更新日時')} onClick={() => changeSortKey(['create_at'])} />
              </tr>
              {/* データ表示 */}
              {filteredPss.map((data) => (
                <tr key={data.updated_at?.toString()}>
                  <td>{getTempState(data.temp_state ?? -1, t)}</td>
                  <td>
                        <div
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{ __html: data.description == null ? "" : data.description.replace(/\r\n|\n/g, '<br />')}}
                        />
                      </td>                          
                  <td>
                    <button
                      type="button"
                      className="text-button"
                      onClick={() => {
                        callModal(
                          getDialogTypeFromTempStateForViewMode(data.temp_state ?? -1),
                          data.temporary_state_history_id ?? -1
                        );
                      }}
                    >
                      <span className="modal-open">
                        <i
                          key={`${data.temporary_state_history_id?.toString() ?? ''} ${getDialogTypeStr(
                            data.temp_state ?? -1,
                            t
                          )}`}
                          className={`fa-solid ${getCheckMarkTypeFromDialogType(
                            getDialogTypeFromTempStateForViewMode(data.temp_state ?? -1)
                          )}`}
                        />
                        {getDialogTypeStr(getDialogTypeFromTempStateForViewMode(data.temp_state ?? -1), t)}
                      </span>
                    </button>
                  </td>
                  <td>
                    {data.update_user_membership_name} {data.update_user}
                  </td>
                  <td>{getFormatDateYYYYMMDD(data.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* ページング */}
          <PageNavi
            current={currentPage}
            pageCount={Math.ceil(psHistoryListApi.data.data.ps_temp_histories.length / (N_DATA_PER_PAGE * 1.0))}
            onChangePage={changeCurrentPage}
          />
        </div>
        <div className="btnfr btnfr4 mb0">
          <div>
            <span className="modal-open text-button" onClick={onClose}>
              <input type="submit" id="" name="" value={t('戻る') ?? '戻る'} className="btn1 lsp1 btnbg-g" />
            </span>{' '}
          </div>
        </div>
      </Modal>
      ({/* HM提出ダイアログ */}
      {activeModal === DialogType.HM_SUBMISSION && (
        <PsHmSubmitDialog
          onClose={() => {
            setActiveModal(DialogType.NONE);
          }}
          displayCloseButton={false}
          versionId={versionId ?? -1}
          state={{ ps_temporary_state_history_id: psTemporaryStateHistoryId.current }}
          setActiveModal={setActiveModal}
          isViewMode
          setRefresh={setRefresh}
          setMessage={(): void => undefined}
        />
      )}
      {/* 確認申請ダイアログ */}
      {activeModal === DialogType.APPLICATION_FOR_CONFIRMATION && (
        <PsCheckAddDialog
          onClose={() => {
            setActiveModal(DialogType.NONE);
          }}
          displayCloseButton={false}
          versionId={versionId ?? -1}
          state={{ ps_temporary_state_history_id: psTemporaryStateHistoryId.current }}
          setActiveModal={setActiveModal}
          isViewMode
          setRefresh={setRefresh}
          setMessage={(): void => undefined}
        />
      )}
      {/* 内容確認（GL）ダイアログ */}
      {activeModal === DialogType.CONTENT_CONFIRMATION_GL && (
        <PsCheckDialog
          onClose={() => {
            setActiveModal(DialogType.NONE);
          }}
          displayCloseButton={false}
          versionId={versionId ?? -1}
          state={{ ps_temporary_state_history_id: psTemporaryStateHistoryId.current }}
          setActiveModal={setActiveModal}
          isViewMode
          setRefresh={setRefresh}
          setMessage={(): void => undefined}
          targetIndex={-1}
          checkNextPs={(): Promise<void> => Promise.resolve()}
        />
      )}
      {/* 内容確認（管理者） */}
      {activeModal === DialogType.CONTENT_CONFIRMATION_MANAGER && (
        <PsApprovalDialog
          onClose={() => {
            setActiveModal(DialogType.NONE);
          }}
          displayCloseButton={false}
          versionId={versionId ?? -1}
          state={{ ps_temporary_state_history_id: psTemporaryStateHistoryId.current }}
          setActiveModal={setActiveModal}
          isViewMode
          setRefresh={setRefresh}
          setMessage={(): void => undefined}
          targetIndex={-1}
          checkNextPs={(): Promise<void> => Promise.resolve()}
        />
      )}
      )
    </>
  );
}

export default PsStateHistoryDialog;
